import React, { memo, useState, useRef, useEffect } from 'react';
import { LayersIcon, CloseIcon, EyeIcon, EyeOffIcon, PlusIcon, MinusIcon, SettingsIcon } from '../../helpers/Icons';
import { CHART_TYPES, INTERVALS } from '../../utils/chart';
import IndicatorPanel from './IndicatorPanel';

const ChartToolbar = ({
  theme,
  interval,
  chartType,
  showIndPanel,
  setShowIndPanel,
  handleInterval,
  handleChartType,
  indicatorControls,
  handleZoomIn,
  handleZoomOut,
  showGrid,
  setShowGrid,
  showBidLine,
  setShowBidLine,
  showOrderLines,
  setShowOrderLines,
  groupedInds,
  timeframes = INTERVALS,
}) => {
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const menuRef = useRef(null);
  const indicatorMenuRef = useRef(null);

  // Close settings menu when clicking outside
  useEffect(() => {
    if (!showSettingsMenu) return;
    const handleOutsideClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showSettingsMenu]);

  useEffect(() => {
    if (!showIndPanel) return;
    const handleOutsideClick = (e) => {
      if (indicatorMenuRef.current && !indicatorMenuRef.current.contains(e.target)) {
        setShowIndPanel(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showIndPanel, setShowIndPanel]);

  const Btn = ({ active, onClick, children, style = {} }) => (
    <button onClick={onClick} style={{
      padding: '3px 8px', borderRadius: 5, border: `1px solid ${active ? theme.primary : 'transparent'}`,
      cursor: 'pointer', fontSize: 10, fontWeight: 600, fontFamily: theme.sans, whiteSpace: 'nowrap',
      background: active ? theme.primaryGlow : 'transparent',
      color: active ? theme.primaryL : theme.textS,
      transition: 'all 0.15s', ...style,
    }}>{children}</button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0, flexWrap: 'wrap', background: theme.surface, minHeight: 34 }}>
      {/* Time Intervals */}
      <div style={{ display: 'flex', gap: 2, overflowX: 'auto', scrollbarWidth: 'none', maxWidth: '100%' }}>
        {timeframes.map(iv => (
          <Btn key={iv} active={interval === iv} onClick={() => handleInterval(iv)}>{iv}</Btn>
        ))}
      </div>
      <div style={{ width: 1, height: 14, background: theme.border, margin: '0 2px', flexShrink: 0 }} />

      {/* Chart Types */}
      <div style={{ display: 'flex', gap: 2 }}>
        {CHART_TYPES.map(ct => (
          <Btn key={ct.id} active={chartType === ct.id} onClick={() => handleChartType(ct.id)}>{ct.label}</Btn>
        ))}
      </div>
      <div style={{ width: 1, height: 14, background: theme.border, margin: '0 2px', flexShrink: 0 }} />

      {/* Zoom Controls */}
      <div style={{ display: 'flex', gap: 2 }}>
        <button onClick={handleZoomOut} style={{
          padding: '3px 6px', borderRadius: 5, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', color: theme.textS, transition: 'all 0.15s'
        }} title="Zoom Out">
          <MinusIcon s={12} c="currentColor" />
        </button>
        <button onClick={handleZoomIn} style={{
          padding: '3px 6px', borderRadius: 5, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', color: theme.textS, transition: 'all 0.15s'
        }} title="Zoom In">
          <PlusIcon s={12} c="currentColor" />
        </button>
      </div>
      <div style={{ width: 1, height: 14, background: theme.border, margin: '0 2px', flexShrink: 0 }} />

      {/* Settings Menu */}
      <div ref={menuRef} style={{ position: 'relative' }}>
        <button onClick={() => setShowSettingsMenu(p => !p)} style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 5,
          border: `1px solid ${showSettingsMenu ? theme.primary : 'transparent'}`, cursor: 'pointer',
          fontSize: 10, fontFamily: theme.sans,
          background: showSettingsMenu ? theme.primaryGlow : 'transparent',
          color: showSettingsMenu ? theme.primaryL : theme.textS,
        }}>
          <SettingsIcon s={12} c="currentColor" />
          Settings
        </button>
        {showSettingsMenu && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 5, width: 180,
            background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 8,
            padding: '10px 12px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: 8,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.textM, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
              Chart Display
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 500, color: theme.text, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={showGrid} onChange={e => setShowGrid(e.target.checked)} style={{ accentColor: theme.primary, cursor: 'pointer' }} />
              Show Grid Lines
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 500, color: theme.text, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={showBidLine} onChange={e => setShowBidLine(e.target.checked)} style={{ accentColor: theme.primary, cursor: 'pointer' }} />
              Show Bid Price Line
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 500, color: theme.text, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={showOrderLines} onChange={e => setShowOrderLines(e.target.checked)} style={{ accentColor: theme.primary, cursor: 'pointer' }} />
              Show Trade Marklines
            </label>
          </div>
        )}
      </div>
      <div style={{ width: 1, height: 14, background: theme.border, margin: '0 2px', flexShrink: 0 }} />

      {/* Technical Indicators Toggle */}
      <div ref={indicatorMenuRef} style={{ position: 'relative' }}>
        <button onClick={() => setShowIndPanel(p => !p)} style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 5,
          border: `1px solid ${showIndPanel ? theme.primary : 'transparent'}`, cursor: 'pointer',
          fontSize: 10, fontFamily: theme.sans,
          background: showIndPanel ? theme.primaryGlow : 'transparent',
          color: showIndPanel ? theme.primaryL : theme.textS,
        }}>
          <LayersIcon size={12} c="currentColor" />
          Indicators
          {indicatorControls.activeList.length > 0 && (
            <span style={{ background: theme.primary, color: '#fff', borderRadius: 8, padding: '0 4px', fontSize: 9, fontWeight: 700 }}>
              {indicatorControls.activeList.length}
            </span>
          )}
        </button>

        {showIndPanel && (
          <IndicatorPanel
            theme={theme}
            setShowIndPanel={setShowIndPanel}
            groupedInds={groupedInds}
            indicatorControls={indicatorControls}
          />
        )}
      </div>

      {/* Active Indicators Pills */}
      {indicatorControls.activeList.map(ind => (
        <div key={ind.uuid} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 5, background: theme.card2, border: `1px solid ${theme.border}` }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: ind.visible ? theme.text : theme.textM }}>{ind.type}</span>
          <button onClick={() => indicatorControls.toggleVisibility(ind.uuid)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            {ind.visible ? <EyeIcon s={10} c={theme.textS} /> : <EyeOffIcon s={10} c={theme.textM} />}
          </button>
          <button onClick={() => indicatorControls.removeIndicator(ind.uuid)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <CloseIcon s={10} c={theme.textM} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default memo(ChartToolbar);
